import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Building2, CheckCircle2, Clock, ImageIcon, Loader2, Plug2, Save, TestTube2, Upload, XCircle, RefreshCw, Lock } from 'lucide-react'
import { type FormEvent, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import {
    ReportMailSenderSettingsCard,
    type ReportMailSenderFieldErrors,
} from '@/app/components/settings/ReportMailSenderSettingsCard'
import { Button } from '@/app/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card'
import { Input } from '@/app/components/ui/input'
import { http } from '@/app/lib/http/client'
import { facturamaService } from '@/app/services/facturama.service'
import {
    settingsRecord,
    settingsService,
    settingValue,
    type ImageSettingValue,
    type ReportMailSenderSetting,
    type UpdateReportMailSenderSetting,
} from '@/app/services/settings.service'
import { wbsService } from '@/app/services/wbs.service'
import { useSessionStore } from '@/app/store/auth/auth.store'

const IMAGE_KEYS = [
    { key: 'logo_main', label: 'Logo principal' },
    { key: 'logo_email', label: 'Logo correos' },
    { key: 'favicon', label: 'Favicon' },
] as const

const TIMEZONES = [
    'America/Mexico_City',
    'America/Monterrey',
    'America/Cancun',
    'America/Chihuahua',
    'America/Hermosillo',
    'America/Tijuana',
]

type ImageKey = typeof IMAGE_KEYS[number]['key']

interface FormState {
    company_name: string
    timezone: string
    'theme.primary_color': string
    'theme.secondary_color': string
    'theme.accent_color': string
    currency_symbol: string
    currency_code: string
    ticket_footer_text: string
    deleted_products_retention_days: string
    'integrations.facturama.username': string
    'integrations.facturama.password': string
    'integrations.facturama.sandbox_mode': boolean
    'integrations.wbs.user': string
    'integrations.wbs.password': string
    'integrations.wbs.base_url': string
    'integrations.wbs.service_commission_amount': string
}

const DEFAULT_FORM: FormState = {
    company_name: '',
    timezone: 'America/Mexico_City',
    'theme.primary_color': '#3B7D3B',
    'theme.secondary_color': '#2563EB',
    'theme.accent_color': '#F59E0B',
    currency_symbol: '$',
    currency_code: 'MXN',
    ticket_footer_text: '¡Gracias por su compra!',
    deleted_products_retention_days: '30',
    'integrations.facturama.username': '',
    'integrations.facturama.password': '',
    'integrations.facturama.sandbox_mode': true,
    'integrations.wbs.user': '',
    'integrations.wbs.password': '',
    'integrations.wbs.base_url': 'http://ws_stage.cloud-services.mx:9192/Service.asmx',
    'integrations.wbs.service_commission_amount': '12.00',
}

export function PlatformSettings() {
    const queryClient = useQueryClient()
    const canEditPlatform = useSessionStore((state) => state.canAction('configuration.configuration.edit'))
    const [form, setForm] = useState<FormState>(DEFAULT_FORM)
    const [savedForm, setSavedForm] = useState<FormState>(DEFAULT_FORM)
    const [reportMailSenders, setReportMailSenders] = useState<ReportMailSenderSetting[]>([])
    const [savedReportMailSenders, setSavedReportMailSenders] = useState<ReportMailSenderSetting[]>([])
    const [reportMailSenderErrors, setReportMailSenderErrors] = useState<Record<number, ReportMailSenderFieldErrors>>({})
    const [uploadingKey, setUploadingKey] = useState<ImageKey | null>(null)

    const { data: settings = [], isLoading } = useQuery({
        queryKey: ['settings'],
        queryFn: settingsService.getSettings,
    })

    const settingMap = useMemo(() => settingsRecord(settings), [settings])

    const reportMailSendersQuery = useQuery({
        queryKey: ['settings', 'report-mail-senders'],
        queryFn: settingsService.getReportMailSenders,
    })

    useEffect(() => {
        if (!settings.length) {
            return
        }

        const nextForm = {
            company_name: settingValue(settings, 'company_name', '') || '',
            timezone: settingValue(settings, 'timezone', 'America/Mexico_City'),
            'theme.primary_color': settingValue(settings, 'theme.primary_color', '#3B7D3B'),
            'theme.secondary_color': settingValue(settings, 'theme.secondary_color', '#2563EB'),
            'theme.accent_color': settingValue(settings, 'theme.accent_color', '#F59E0B'),
            currency_symbol: settingValue(settings, 'currency_symbol', '$'),
            currency_code: settingValue(settings, 'currency_code', 'MXN'),
            ticket_footer_text: settingValue(settings, 'ticket_footer_text', '¡Gracias por su compra!'),
            low_stock_threshold: String(settingValue(settings, 'low_stock_threshold', 5)),
            deleted_products_retention_days: String(settingValue(settings, 'deleted_products_retention_days', 30)),
            'integrations.facturama.username': settingValue(settings, 'integrations.facturama.username', ''),
            'integrations.facturama.password': '',
            'integrations.facturama.sandbox_mode': settingValue(settings, 'integrations.facturama.sandbox_mode', true),
            'integrations.facturama.expedition_place': settingValue(settings, 'integrations.facturama.expedition_place', '64257'),
            'integrations.facturama.cfdi_use': settingValue(settings, 'integrations.facturama.cfdi_use', 'G03'),
            'integrations.wbs.user': settingValue(settings, 'integrations.wbs.user', ''),
            'integrations.wbs.password': '',
            'integrations.wbs.base_url': settingValue(settings, 'integrations.wbs.base_url', 'http://ws_stage.cloud-services.mx:9192/Service.asmx'),
            'integrations.wbs.service_commission_amount': String(settingValue(settings, 'integrations.wbs.service_commission_amount', 12)),
        }

        setForm(nextForm)
        setSavedForm(nextForm)
    }, [settings])

    useEffect(() => {
        if (!reportMailSendersQuery.data) {
            return
        }

        setReportMailSenders(editableReportMailSenders(reportMailSendersQuery.data))
        setSavedReportMailSenders(reportMailSendersQuery.data)
        setReportMailSenderErrors({})
    }, [reportMailSendersQuery.data])

    const { data: facturamaProfile, isLoading: isLoadingProfile } = useQuery({
        queryKey: ['facturama-profile'],
        queryFn: facturamaService.getProfile,
        retry: false,
    })

    const updateMutation = useMutation({
        mutationFn: settingsService.updatePlatformSettings,
        onSuccess: (result) => {
            queryClient.setQueryData(['settings'], result.settings)
            queryClient.setQueryData(['settings', 'report-mail-senders'], result.report_mail_senders)
            toast.success('Configuracion actualizada')
        },
        onError: (error) => {
            toast.error(apiErrorMessage(error, 'No se pudo guardar la configuracion'))
        },
    })

    const [connectionStatus, setConnectionStatus] = useState<'idle' | 'success' | 'error'>('idle')

    const testConnectionMutation = useMutation({
        mutationFn: () => http.post('/integrations/facturama/test-connection', {
            username: form['integrations.facturama.username'],
            password: form['integrations.facturama.password'],
            sandbox_mode: form['integrations.facturama.sandbox_mode'],
        }),
        onSuccess: (res) => {
            setConnectionStatus('success')
            toast.success('Conexión con Facturama exitosa')
            if (res.data?.data) {
                queryClient.setQueryData(['facturama-profile'], res.data.data)
            }
        },
        onError: (error) => {
            setConnectionStatus('error')
            toast.error(apiErrorMessage(error, 'No se pudo conectar con Facturama'))
        },
    })

    const [wbsConnectionStatus, setWbsConnectionStatus] = useState<'idle' | 'success' | 'error'>('idle')

    const hasStoredWbsCredentials = Boolean(
        form['integrations.wbs.user']
        && form['integrations.wbs.base_url']
        && settingMap['integrations.wbs.password']?.is_configured,
    )

    const {
        data: wbsBalanceResponse,
        isFetching: isFetchingWbsBalance,
        isError: isWbsBalanceError,
        refetch: refetchWbsBalance,
    } = useQuery({
        queryKey: ['wbs-balance'],
        queryFn: wbsService.getBalance,
        enabled: !isLoading && hasStoredWbsCredentials,
        retry: false,
    })

    const wbsBalance = wbsBalanceResponse?.data

    const wbsTestConnectionMutation = useMutation({
        mutationFn: () => http.post('/integrations/wbs/test-connection', {
            user: form['integrations.wbs.user'],
            password: form['integrations.wbs.password'],
            base_url: form['integrations.wbs.base_url'],
        }),
        onSuccess: () => {
            setWbsConnectionStatus('success')
            toast.success('Conexión con WBS exitosa')
        },
        onError: (error) => {
            setWbsConnectionStatus('error')
            toast.error(apiErrorMessage(error, 'No se pudo conectar con WBS'))
        },
    })

    const syncSatMutation = useMutation({
        mutationFn: () => {
            const limit = window.prompt('¿Cuántos registros deseas sincronizar? (Deja en blanco para todos)')
            const payload = limit ? { limit: Number(limit) } : {}
            return http.post('/integrations/facturama/sync-sat-keys', payload)
        },
        onSuccess: (response) => {
            toast.success(response.data?.message || 'Sincronización iniciada/completada con éxito')
        },
        onError: (error) => {
            toast.error(apiErrorMessage(error, 'No se pudo sincronizar claves SAT'))
        },
    })

    const uploadMutation = useMutation({
        mutationFn: ({ key, file }: { key: ImageKey; file: File }) => settingsService.uploadAsset(key, file),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['settings'] })
            toast.success('Imagen actualizada')
        },
        onError: (error) => {
            toast.error(apiErrorMessage(error, 'No se pudo subir la imagen'))
        },
        onSettled: () => setUploadingKey(null),
    })

    const handleReportMailSenderChange = (
        sucursalId: number,
        field:
            | 'report_mail_from_address'
            | 'report_mail_from_name'
            | 'report_mail_username'
            | 'report_mail_password',
        value: string,
    ) => {
        setReportMailSenders((current) => current.map((sender) => (
            sender.id === sucursalId
                ? {
                    ...sender,
                    [field]: value,
                    ...(field === 'report_mail_password' ? { clear_report_mail_password: false } : {}),
                }
                : sender
        )))
        setReportMailSenderErrors((current) => {
            if (!current[sucursalId]) {
                return current
            }

            const next = { ...current }
            delete next[sucursalId]
            return next
        })
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (!canEditPlatform) {
            toast.error('No tienes permiso para editar la configuración de plataforma')
            return
        }

        const nextForm = {
            ...form,
            company_name: form.company_name.trim(),
        }
        const settingsPayload = changedSettingsPayload(nextForm, savedForm)
        const senderErrors = validateReportMailSenders(reportMailSenders, savedReportMailSenders)
        setReportMailSenderErrors(senderErrors)

        if (Object.keys(senderErrors).length > 0) {
            toast.error('Revisa la configuración de correo de las sucursales')
            return
        }

        const reportMailSenderPayload = changedReportMailSendersPayload(
            reportMailSenders,
            savedReportMailSenders,
        )

        if (Object.keys(settingsPayload).length === 0 && reportMailSenderPayload.length === 0) {
            toast.info('No hay cambios por guardar')
            return
        }

        try {
            const result = await updateMutation.mutateAsync({
                ...(Object.keys(settingsPayload).length > 0 ? { settings: settingsPayload } : {}),
                ...(reportMailSenderPayload.length > 0 ? { report_mail_senders: reportMailSenderPayload } : {}),
            })
            setForm(nextForm)
            setSavedForm(nextForm)
            setReportMailSenders(editableReportMailSenders(result.report_mail_senders))
            setSavedReportMailSenders(result.report_mail_senders)
            setReportMailSenderErrors({})
        } catch {
            // El toast se emite en onError.
        }
    }

    const handleImageChange = async (key: ImageKey, file?: File) => {
        if (!file) {
            return
        }

        setUploadingKey(key)

        try {
            await uploadMutation.mutateAsync({ key, file })
        } catch {
            // El toast se emite en onError.
        }
    }

    return (
        <div className="min-h-full bg-background px-4 py-6 sm:px-6 lg:px-8">
            <div className="mx-auto flex max-w-6xl flex-col gap-6">
                <div className="flex flex-col gap-2">
                    <h1 className="text-2xl font-semibold text-foreground">Configuracion de plataforma</h1>
                    <p className="text-sm text-muted-foreground">
                        Ajusta la identidad visual que usa el punto de venta y las pantallas de acceso.
                    </p>
                </div>

                {isLoading ? (
                    <div className="flex min-h-64 items-center justify-center rounded-lg border border-card-border bg-card">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <ReportMailSenderSettingsCard
                            senders={reportMailSenders}
                            errors={reportMailSenderErrors}
                            isLoading={reportMailSendersQuery.isLoading}
                            isError={reportMailSendersQuery.isError}
                            isDisabled={!canEditPlatform || updateMutation.isPending}
                            onChange={handleReportMailSenderChange}
                            onRetry={() => reportMailSendersQuery.refetch()}
                        />

                        <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
                        <div className="space-y-6">
                            <Card className="rounded-lg">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 text-base">
                                        <Building2 className="h-5 w-5 text-primary" />
                                        Identidad
                                    </CardTitle>
                                    <CardDescription>Nombre visible y recursos principales del POS.</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-5">
                                    <div className="space-y-2">
                                        <label htmlFor="company_name" className="text-sm font-medium text-card-foreground">
                                            Nombre comercial
                                        </label>
                                        <Input
                                            id="company_name"
                                            value={form.company_name}
                                            onChange={(event) => setForm((current) => ({
                                                ...current,
                                                company_name: event.target.value,
                                            }))}
                                            placeholder="Farmacias Salud Plus"
                                        />
                                    </div>

                                    <div className="grid gap-4 md:grid-cols-3">
                                        {IMAGE_KEYS.map(({ key, label }) => {
                                            const image = settingMap[key]?.value as ImageSettingValue | null | undefined
                                            const isUploading = uploadingKey === key

                                            return (
                                                <div key={key} className="rounded-lg border border-card-border bg-card p-4">
                                                    <div className="flex h-24 items-center justify-center rounded-md border border-dashed border-card-border bg-card-foreground/5">
                                                        {image?.url ? (
                                                            <img src={image.url} alt={label} className="max-h-20 max-w-full object-contain" />
                                                        ) : (
                                                            <ImageIcon className="h-8 w-8 text-card-description" />
                                                        )}
                                                    </div>
                                                    <div className="mt-3 space-y-2">
                                                        <p className="text-sm font-medium text-card-foreground">{label}</p>
                                                        <Button asChild  className="w-full cursor-pointer">
                                                            <label>
                                                                {isUploading ? (
                                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                                ) : (
                                                                    <Upload className="h-4 w-4" />
                                                                )}
                                                                Subir
                                                                <input
                                                                    type="file"
                                                                    accept="image/png,image/jpeg,image/webp"
                                                                    className="sr-only"
                                                                    disabled={isUploading}
                                                                    onClick={(event) => {
                                                                        event.currentTarget.value = ''
                                                                    }}
                                                                    onChange={(event) => handleImageChange(key, event.target.files?.[0])}
                                                                />
                                                            </label>
                                                        </Button>
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </CardContent>
                            </Card>
                            {/**
                            <Card className="rounded-lg">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 text-base">
                                        <Palette className="h-5 w-5 text-primary" />
                                        Paleta de colores
                                    </CardTitle>
                                    <CardDescription>Estos colores se aplican al tema activo del POS.</CardDescription>
                                </CardHeader>
                                <CardContent className="grid gap-4 md:grid-cols-3">
                                    {COLOR_KEYS.map(({ key, label }) => (
                                        <ColorField
                                            key={key}
                                            label={label}
                                            value={form[key]}
                                            onChange={(value) => setForm((current) => ({ ...current, [key]: value }))}
                                        />
                                    ))}
                                </CardContent>
                            </Card>
                            <Card className="rounded-lg">
                                <CardHeader>
                                    <CardTitle className="text-base">Vista previa</CardTitle>
                                    <CardDescription>Referencia rapida del tema configurado.</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="rounded-lg border border-card-border bg-card p-4">
                                        <div className="flex items-center gap-3">
                                            <div
                                                className="h-12 w-12 rounded-md"
                                                style={{ backgroundColor: form['theme.primary_color'] }}
                                            />
                                            <div>
                                                <p className="text-sm font-semibold text-card-foreground">
                                                    {form.company_name || 'Farmacias Salud Plus'}
                                                </p>
                                                <p className="text-xs text-card-description">{form.timezone}</p>
                                            </div>
                                        </div>
                                        <div className="mt-4 grid grid-cols-3 gap-2">
                                            {COLOR_KEYS.map(({ key, label }) => (
                                                <div key={key} className="space-y-1">
                                                    <div className="h-8 rounded-md" style={{ backgroundColor: form[key] }} />
                                                    <p className="text-xs text-card-description">{label}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                            */}
                            <Card className="rounded-lg">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 text-base">
                                        <Plug2 className="h-5 w-5 text-primary" />
                                        Integraciones
                                    </CardTitle>
                                    <CardDescription>Conecta servicios externos con la plataforma.</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="rounded-lg border border-card-border p-4 space-y-4">
                                        <div className="flex items-center gap-3">
                                            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-blue-50">
                                                <span className="text-xs font-bold text-blue-600">F</span>
                                            </div>
                                            <div>
                                                <p className="text-sm font-semibold text-card-foreground">Facturama</p>
                                                <p className="text-xs text-card-description">Facturación electrónica CFDI 4.0</p>
                                            </div>
                                        </div>

                                        <div className="space-y-3">
                                            <div className="space-y-1.5">
                                                <label htmlFor="facturama_username" className="text-xs font-medium text-card-foreground">
                                                    Usuario
                                                </label>
                                                <Input
                                                    id="facturama_username"
                                                    value={form['integrations.facturama.username']}
                                                    onChange={(e) => setForm((c) => ({ ...c, 'integrations.facturama.username': e.target.value }))}
                                                    placeholder="usuario_facturama"
                                                    autoComplete="off"
                                                />
                                            </div>
                                            <div className="space-y-1.5">
                                                <label htmlFor="facturama_password" className="text-xs font-medium text-card-foreground">
                                                    Contraseña
                                                </label>
                                                <Input
                                                    id="facturama_password"
                                                    type="password"
                                                    value={form['integrations.facturama.password']}
                                                    onChange={(e) => { setConnectionStatus('idle'); setForm((c) => ({ ...c, 'integrations.facturama.password': e.target.value })) }}
                                                    placeholder="••••••••"
                                                    autoComplete="new-password"
                                                />
                                            </div>
                                            <div className="flex items-center justify-between rounded-md border border-card-border px-3 py-2.5">
                                                <div>
                                                    <p className="text-xs font-medium text-card-foreground">Modo sandbox</p>
                                                    <p className="text-xs text-card-description">Usar entorno de pruebas de Facturama</p>
                                                </div>
                                                <button
                                                    type="button"
                                                    role="switch"
                                                    aria-checked={form['integrations.facturama.sandbox_mode']}
                                                    onClick={() => { setConnectionStatus('idle'); setForm((c) => ({ ...c, 'integrations.facturama.sandbox_mode': !c['integrations.facturama.sandbox_mode'] })) }}
                                                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${form['integrations.facturama.sandbox_mode'] ? 'bg-primary' : 'bg-accent'
                                                        }`}
                                                >
                                                    <span
                                                        className={`inline-block h-4 w-4 transform rounded-full bg-card shadow-sm transition-transform ${form['integrations.facturama.sandbox_mode'] ? 'translate-x-4' : 'translate-x-0.5'
                                                            }`}
                                                    />
                                                </button>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2 pt-1">
                                            <Button
                                                type="button"
                                                
                                                size="sm"
                                                disabled={testConnectionMutation.isPending}
                                                onClick={() => testConnectionMutation.mutate()}
                                                className="h-8 gap-1.5 text-xs"
                                            >
                                                {testConnectionMutation.isPending ? (
                                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                ) : (
                                                    <TestTube2 className="h-3.5 w-3.5" />
                                                )}
                                                Probar conexión
                                            </Button>
                                            {connectionStatus === 'success' && (
                                                <span className="flex items-center gap-1 text-xs text-emerald-600">
                                                    <CheckCircle2 className="h-3.5 w-3.5" /> Conectado
                                                </span>
                                            )}
                                            {connectionStatus === 'error' && (
                                                <span className="flex items-center gap-1 text-xs text-red-500">
                                                    <XCircle className="h-3.5 w-3.5" /> Sin conexión
                                                </span>
                                            )}
                                        </div>

                                        <div className="flex flex-col gap-2 pt-2 border-t border-card-border">
                                            <div>
                                                <p className="text-xs font-medium text-card-foreground">Sincronización de Claves SAT</p>
                                                <p className="text-xs text-card-description">Actualiza las descripciones de claves SAT genéricas con el catálogo real de Facturama.</p>
                                            </div>
                                            <Button
                                                type="button"
                                                
                                                size="sm"
                                                disabled={syncSatMutation.isPending}
                                                onClick={() => syncSatMutation.mutate()}
                                                className="h-8 gap-1.5 text-xs max-w-fit"
                                            >
                                                {syncSatMutation.isPending ? (
                                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                ) : (
                                                    <RefreshCw className="h-3.5 w-3.5" />
                                                )}
                                                Sincronizar claves
                                            </Button>
                                        </div>

                                        <div className="flex flex-col gap-2 pt-2 border-t border-card-border">
                                            <div>
                                                <p className="text-xs font-medium text-card-foreground">Datos Fiscales - Facturacion</p>
                                                <p className="text-xs text-card-description">Agrega los datos fiscales de la farmacia para generar las facturas de las ventas.</p>
                                            </div>
                                            <div className="space-y-3 bg-card-foreground/5 p-4 rounded-lg border border-card-border">
                                                {isLoadingProfile ? (
                                                    <div className="flex items-center gap-2 text-sm text-card-description">
                                                        <Loader2 className="h-4 w-4 animate-spin" />
                                                        Cargando datos fiscales desde Facturama...
                                                    </div>
                                                ) : facturamaProfile ? (
                                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                                        <div>
                                                            <p className="font-medium text-card-description text-xs">RFC Emisor</p>
                                                            <p className="font-semibold text-card-foreground">{facturamaProfile.Rfc}</p>
                                                        </div>
                                                        <div>
                                                            <p className="font-medium text-card-description text-xs">Razón Social</p>
                                                            <p className="font-semibold text-card-foreground">{facturamaProfile.TaxName}</p>
                                                        </div>
                                                        <div>
                                                            <p className="font-medium text-card-description text-xs">Régimen Fiscal</p>
                                                            <p className="font-semibold text-card-foreground">{facturamaProfile.FiscalRegime}</p>
                                                        </div>
                                                        <div>
                                                            <p className="font-medium text-card-description text-xs">Código Postal (Expedición)</p>
                                                            <p className="font-semibold text-card-foreground">
                                                                {facturamaProfile.IssuedIn?.ZipCode || facturamaProfile.TaxAddress?.ZipCode || 'No configurado'}
                                                            </p>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="text-sm text-amber-600">
                                                        No se pudieron cargar los datos fiscales. Verifica tus credenciales.
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="rounded-lg border border-card-border p-4 space-y-4">
                                        <div className="flex items-center gap-3">
                                            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-orange-50">
                                                <span className="text-xs font-bold text-orange-600">W</span>
                                            </div>
                                            <div>
                                                <p className="text-sm font-semibold text-card-foreground">WBS Recarga-me</p>
                                                <p className="text-xs text-card-description">Recargas de tiempo aire y servicios</p>

                                            </div>

                                        </div>

                                        <div className="space-y-3">
                                            <div className="space-y-1.5">
                                                <label htmlFor="wbs_user" className="text-xs font-medium text-card-foreground">
                                                    Usuario
                                                </label>
                                                <Input
                                                    id="wbs_user"
                                                    value={form['integrations.wbs.user']}
                                                    onChange={(e) => { setWbsConnectionStatus('idle'); setForm((c) => ({ ...c, 'integrations.wbs.user': e.target.value })) }}
                                                    placeholder="6144135400"
                                                />
                                            </div>
                                            <div className="space-y-1.5">
                                                <label htmlFor="wbs_password" className="text-xs font-medium text-card-foreground">
                                                    Contraseña
                                                </label>
                                                <Input
                                                    id="wbs_password"
                                                    type="password"
                                                    value={form['integrations.wbs.password']}
                                                    onChange={(e) => { setWbsConnectionStatus('idle'); setForm((c) => ({ ...c, 'integrations.wbs.password': e.target.value })) }}
                                                    placeholder="••••••••"
                                                />
                                            </div>
                                            <div className="space-y-1.5">
                                                <label htmlFor="wbs_base_url" className="text-xs font-medium text-card-foreground">
                                                    URL del Servicio
                                                </label>
                                                <Input
                                                    id="wbs_base_url"
                                                    value={form['integrations.wbs.base_url']}
                                                    onChange={(e) => { setWbsConnectionStatus('idle'); setForm((c) => ({ ...c, 'integrations.wbs.base_url': e.target.value })) }}
                                                    placeholder="http://ws_stage.cloud-services.mx:9192/Service.asmx"
                                                />
                                            </div>
                                            <div className="space-y-1.5">
                                                <label htmlFor="wbs_service_commission" className="text-xs font-medium text-slate-700">
                                                    Comisión por pago de servicio
                                                </label>
                                                <div className="relative">
                                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">$</span>
                                                    <Input
                                                        id="wbs_service_commission"
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        className="pl-7"
                                                        value={form['integrations.wbs.service_commission_amount']}
                                                        onChange={(e) => setForm((current) => ({
                                                            ...current,
                                                            'integrations.wbs.service_commission_amount': e.target.value,
                                                        }))}
                                                    />
                                                </div>
                                                <p className="text-xs text-slate-500">Se suma al monto del servicio; no aplica a recargas.</p>
                                            </div>
                                        </div>

                                        <div className="rounded-md border border-emerald-100 bg-emerald-50/60 p-3">
                                            <div className="flex items-start justify-between gap-3">
                                                <div>
                                                    <p className="text-xs font-medium text-emerald-700">Saldo disponible en Recarga-Me</p>
                                                    <p className="mt-1 text-2xl font-semibold text-emerald-900">
                                                        {wbsBalance ? `$${Number(wbsBalance.Balance || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                                                    </p>
                                                    <p className="mt-1 max-w-xl text-xs text-emerald-800/80">
                                                        Crédito disponible con el proveedor para procesar recargas y pagos de servicios. No corresponde al efectivo de caja ni al total de ventas del POS.
                                                    </p>
                                                </div>
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    disabled={!hasStoredWbsCredentials || isFetchingWbsBalance}
                                                    onClick={() => refetchWbsBalance()}
                                                >
                                                    <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${isFetchingWbsBalance ? 'animate-spin' : ''}`} />
                                                    Actualizar saldo
                                                </Button>
                                            </div>
                                            {wbsBalance && (
                                                <div className="mt-3 border-t border-emerald-100 pt-3">
                                                    <p className="mb-2 text-xs font-medium text-slate-600">Desglose reportado por Recarga-Me</p>
                                                    <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
                                                        <span>Saldo inicial: ${wbsBalance.Saldo_Inicial ?? '0'}</span>
                                                        <span>Compras: ${wbsBalance.Compras ?? '0'}</span>
                                                        <span>Ventas: ${wbsBalance.Ventas ?? '0'}</span>
                                                        <span>Comisión: ${wbsBalance.Comision ?? '0'}</span>
                                                    </div>
                                                    <p className="mt-2 text-[11px] text-slate-500">
                                                        Estos conceptos son informativos y el proveedor no define su fórmula en el manual de integración.
                                                    </p>
                                                </div>
                                            )}
                                            {isWbsBalanceError && (
                                                <p className="mt-2 text-xs text-red-600">No se pudo consultar el saldo. Verifica las credenciales guardadas y vuelve a intentar.</p>
                                            )}
                                            {!hasStoredWbsCredentials && (
                                                <p className="mt-2 text-xs text-slate-500">Guarda las credenciales WBS para consultar el saldo.</p>
                                            )}
                                        </div>

                                        <div className="flex items-center gap-2 pt-1">
                                            <Button
                                                type="button"
                                                
                                                size="sm"
                                                disabled={wbsTestConnectionMutation.isPending}
                                                onClick={() => wbsTestConnectionMutation.mutate()}
                                                className="h-8 gap-1.5 text-xs"
                                            >
                                                {wbsTestConnectionMutation.isPending ? (
                                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                ) : (
                                                    <TestTube2 className="h-3.5 w-3.5" />
                                                )}
                                                Probar conexión
                                            </Button>
                                            {wbsConnectionStatus === 'success' && (
                                                <span className="flex items-center gap-1 text-xs text-emerald-600">
                                                    <CheckCircle2 className="h-3.5 w-3.5" /> Conectado
                                                </span>
                                            )}
                                            {wbsConnectionStatus === 'error' && (
                                                <span className="flex items-center gap-1 text-xs text-red-500">
                                                    <XCircle className="h-3.5 w-3.5" /> Sin conexión
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>


                        </div>

                        <div className="space-y-6">
                            <Card className="rounded-lg">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 text-base">
                                        <Clock className="h-5 w-5 text-primary" />
                                        Regional
                                    </CardTitle>
                                    <CardDescription>Configuraciones base para la compania.</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-2">
                                    <label htmlFor="timezone" className="text-sm font-medium text-card-foreground">
                                        Zona horaria
                                    </label>
                                    <select
                                        id="timezone"
                                        value={form.timezone}
                                        onChange={(event) => setForm((current) => ({
                                            ...current,
                                            timezone: event.target.value,
                                        }))}
                                        className="flex h-10 w-full rounded-md border border-card-border bg-card px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                                    >
                                        {TIMEZONES.map((timezone) => (
                                            <option key={timezone} value={timezone}>
                                                {timezone}
                                            </option>
                                        ))}
                                    </select>
                                </CardContent>
                            </Card>

                            <Card className="rounded-lg">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 text-base">
                                        <Building2 className="h-5 w-5 text-primary" />
                                        Punto de Venta
                                    </CardTitle>
                                    <CardDescription>Configuraciones de moneda, tickets e inventario.</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <label htmlFor="currency_symbol" className="text-sm font-medium text-card-foreground">
                                                Símbolo de moneda
                                            </label>
                                            <Input
                                                id="currency_symbol"
                                                value={form.currency_symbol}
                                                onChange={(e) => setForm((c) => ({ ...c, currency_symbol: e.target.value }))}
                                                placeholder="$"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label htmlFor="currency_code" className="text-sm font-medium text-card-foreground">
                                                Código de moneda
                                            </label>
                                            <Input
                                                id="currency_code"
                                                value={form.currency_code}
                                                onChange={(e) => setForm((c) => ({ ...c, currency_code: e.target.value }))}
                                                placeholder="MXN"
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <label htmlFor="ticket_footer_text" className="text-sm font-medium text-card-foreground">
                                            Texto al final del ticket
                                        </label>
                                        <Input
                                            id="ticket_footer_text"
                                            value={form.ticket_footer_text}
                                            onChange={(e) => setForm((c) => ({ ...c, ticket_footer_text: e.target.value }))}
                                            placeholder="¡Gracias por su compra!"
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <label htmlFor="deleted_products_retention_days" className="text-sm font-medium text-card-foreground">
                                                Días retención papelera
                                            </label>
                                            <Input
                                                id="deleted_products_retention_days"
                                                type="number"
                                                value={form.deleted_products_retention_days}
                                                onChange={(e) => setForm((c) => ({ ...c, deleted_products_retention_days: e.target.value }))}
                                                placeholder="30"
                                            />
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>


                            <PosPinSettings />


                        </div>
                        </div>

                        <div className="fixed bottom-4 right-4 flex justify-end">
                            <Button type="submit" size="lg" disabled={!canEditPlatform || updateMutation.isPending}>
                                {updateMutation.isPending ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <Save className="h-4 w-4" />
                                )}
                                Guardar cambios
                            </Button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    )
}

function validateReportMailSenders(
    senders: ReportMailSenderSetting[],
    savedSenders: ReportMailSenderSetting[],
): Record<number, ReportMailSenderFieldErrors> {
    const errors: Record<number, ReportMailSenderFieldErrors> = {}
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    const savedById = new Map(savedSenders.map((sender) => [sender.id, sender]))

    for (const sender of senders) {
        const address = sender.report_mail_from_address?.trim() ?? ''
        const name = sender.report_mail_from_name?.trim() ?? ''
        const username = sender.report_mail_username?.trim() ?? ''
        const password = sender.report_mail_password ?? ''
        const saved = savedById.get(sender.id)
        const senderChanged = !saved
            || nullableTrimmed(sender.report_mail_from_address) !== nullableTrimmed(saved.report_mail_from_address)
            || nullableTrimmed(sender.report_mail_from_name) !== nullableTrimmed(saved.report_mail_from_name)
            || nullableTrimmed(sender.report_mail_username) !== nullableTrimmed(saved.report_mail_username)
            || password.trim() !== ''
            || Boolean(sender.clear_report_mail_password)
        const fieldErrors: ReportMailSenderFieldErrors = {}

        if (!senderChanged) {
            continue
        }

        const passwordAvailable = Boolean(
            password.trim()
            || (sender.report_mail_password_configured && !sender.clear_report_mail_password),
        )
        const isBeingConfigured = Boolean(address || name || username || passwordAvailable)

        if (!isBeingConfigured) {
            continue
        }

        if (!username) {
            fieldErrors.username = 'Completa el usuario SMTP.'
        }

        if (!passwordAvailable) {
            fieldErrors.password = 'Completa la contraseña SMTP.'
        }

        if (!address) {
            fieldErrors.address = 'Completa el correo remitente.'
        }

        if (!name) {
            fieldErrors.name = 'Completa el nombre remitente.'
        }

        if (address && (!emailPattern.test(address) || address.length > 255)) {
            fieldErrors.address = 'Ingresa un correo remitente válido de hasta 255 caracteres.'
        }

        if (name.length > 255) {
            fieldErrors.name = 'El nombre remitente no puede exceder 255 caracteres.'
        }

        if (username.length > 255) {
            fieldErrors.username = 'El usuario SMTP no puede exceder 255 caracteres.'
        }

        if (password.length > 1024) {
            fieldErrors.password = 'La contraseña SMTP no puede exceder 1024 caracteres.'
        }

        if (fieldErrors.address || fieldErrors.name || fieldErrors.username || fieldErrors.password) {
            errors[sender.id] = fieldErrors
        }
    }

    return errors
}

function changedReportMailSendersPayload(
    senders: ReportMailSenderSetting[],
    savedSenders: ReportMailSenderSetting[],
): UpdateReportMailSenderSetting[] {
    const savedById = new Map(savedSenders.map((sender) => [sender.id, sender]))

    return senders.flatMap((sender) => {
        const saved = savedById.get(sender.id)
        const address = nullableTrimmed(sender.report_mail_from_address)
        const name = nullableTrimmed(sender.report_mail_from_name)
        const username = nullableTrimmed(sender.report_mail_username)
        const password = sender.report_mail_password ?? ''
        const clearPassword = Boolean(sender.clear_report_mail_password)
        const savedAddress = nullableTrimmed(saved?.report_mail_from_address)
        const savedName = nullableTrimmed(saved?.report_mail_from_name)
        const savedUsername = nullableTrimmed(saved?.report_mail_username)

        if (saved
            && address === savedAddress
            && name === savedName
            && username === savedUsername
            && password.trim() === ''
            && !clearPassword) {
            return []
        }

        const payload: UpdateReportMailSenderSetting = {
            sucursal_id: sender.id,
            report_mail_from_address: address,
            report_mail_from_name: name,
            report_mail_username: username,
        }

        if (password.trim() !== '') {
            payload.report_mail_password = password
        } else if (clearPassword) {
            payload.clear_report_mail_password = true
        }

        return [payload]
    })
}

function editableReportMailSenders(
    senders: ReportMailSenderSetting[],
): ReportMailSenderSetting[] {
    return senders.map((sender) => ({
        ...sender,
        report_mail_password: '',
        clear_report_mail_password: false,
    }))
}

function nullableTrimmed(value: string | null | undefined): string | null {
    const trimmed = value?.trim() ?? ''

    return trimmed === '' ? null : trimmed
}

function changedSettingsPayload(form: FormState, savedForm: FormState): Record<string, string | number | boolean | null> {
    const payload: Record<string, string | number | boolean | null> = {}
    const keys = Object.keys(form) as Array<keyof FormState>

    for (const key of keys) {
        if (form[key] === savedForm[key]) {
            continue
        }

        if (key === 'integrations.wbs.service_commission_amount') {
            payload[key] = Number(form[key])
            continue
        }

        payload[key] = key === 'company_name' && form[key] === '' ? null : form[key]
    }

    return payload
}

export function PosPinSettings() {
    const [password, setPassword] = useState('')
    const [pin, setPin] = useState('')
    const [pinConfirmation, setPinConfirmation] = useState('')

    const mutation = useMutation({
        mutationFn: settingsService.updatePosPin,
        onSuccess: () => {
            toast.success('PIN de caja actualizado exitosamente')
            setPassword('')
            setPin('')
            setPinConfirmation('')
        },
        onError: (error) => {
            toast.error(apiErrorMessage(error, 'No se pudo actualizar el PIN'))
        }
    })

    const handleSubmit = () => {
        if (!password || !pin || !pinConfirmation) {
            toast.error('Todos los campos son obligatorios')
            return
        }
        if (pin !== pinConfirmation) {
            toast.error('El PIN y su confirmación no coinciden')
            return
        }
        if (pin.length < 4) {
            toast.error('El PIN debe tener al menos 4 dígitos')
            return
        }
        mutation.mutate({ password, pin, pin_confirmation: pinConfirmation })
    }

    return (
        <Card className="rounded-lg">
            <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                    <Lock className="h-5 w-5 text-primary" />
                    Seguridad de Caja
                </CardTitle>
                <CardDescription>
                    Configura el PIN global para asignar o cambiar de caja. (Solo administradores).
                </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                    <label className="text-sm font-medium text-card-foreground">Contraseña actual</label>
                    <Input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Tu contraseña actual"
                    />
                </div>
                <div className="space-y-2">
                    <label className="text-sm font-medium text-card-foreground">Nuevo PIN</label>
                    <Input
                        type="password"
                        maxLength={8}
                        pattern="\d+"
                        value={pin}
                        onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                        placeholder="Ej. 1234"
                        className="font-mono tracking-widest"
                    />
                </div>
                <div className="space-y-2">
                    <label className="text-sm font-medium text-card-foreground">Confirmar PIN</label>
                    <Input
                        type="password"
                        maxLength={8}
                        pattern="\d+"
                        value={pinConfirmation}
                        onChange={(e) => setPinConfirmation(e.target.value.replace(/\D/g, ''))}
                        placeholder="Ej. 1234"
                        className="font-mono tracking-widest"
                    />
                </div>
            </CardContent>
            <div className="flex justify-end p-6 pt-0">
                <Button
                    type="button"
                    size="lg"
                    onClick={handleSubmit}
                    disabled={mutation.isPending}
                >
                    {mutation.isPending ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                        <Save className="mr-2 h-4 w-4" />
                    )}
                    Actualizar PIN
                </Button>
            </div>
        </Card>
    )
}

function apiErrorMessage(error: unknown, fallback: string): string {
    if (
        typeof error === 'object'
        && error !== null
        && 'response' in error
        && typeof error.response === 'object'
        && error.response !== null
        && 'data' in error.response
        && typeof error.response.data === 'object'
        && error.response.data !== null
        && 'message' in error.response.data
        && typeof error.response.data.message === 'string'
    ) {
        return error.response.data.message
    }

    return fallback
}
